/**
 * Taxonomy — public facade. Assembles the registries into the designer's
 * decision chain, validates everything at load, and exposes the one engine
 * instance the UI (and later the OpenAI prompt builder and Design Engine
 * consumers) read from.
 *
 *   documentType → industry → category → purpose → audience
 *     → brandPersonality → visualTheme → layoutStyle → imageStyle
 *     → colorMood → typographyStyle
 */

import { createTaxonomyEngine } from './taxonomyEngine';
import { validateRegistry, slugify } from './schema';
import { INDUSTRIES } from './registries/industries';
import { PURPOSES } from './registries/purposes';
import { AUDIENCES } from './registries/audiences';
import { BRAND_PERSONALITIES } from './registries/brandPersonalities';
import { VISUAL_THEMES } from './registries/visualThemes';
import { LAYOUT_STYLES } from './registries/layoutStyles';
import { IMAGE_STYLES } from './registries/imageStyles';
import { COLOR_MOODS } from './registries/colorMoods';
import { TYPOGRAPHY_STYLES } from './registries/typographyStyles';

/** The decision chain, in order. documentType values come from Phase 1/2. */
export const TAXONOMY_FIELDS = [
  { id: 'documentType', label: 'Document Type' },
  { id: 'industry', label: 'Industry' },
  { id: 'category', label: 'Category', requires: ['industry'] },
  { id: 'purpose', label: 'Purpose' },
  { id: 'audience', label: 'Audience' },
  { id: 'brandPersonality', label: 'Brand Personality' },
  { id: 'visualTheme', label: 'Visual Theme' },
  { id: 'layoutStyle', label: 'Layout Style' },
  { id: 'imageStyle', label: 'Image Style' },
  { id: 'colorMood', label: 'Color Mood' },
  { id: 'typographyStyle', label: 'Typography Style' },
];

/** Expand each industry's category names into bound taxonomy items. */
const CATEGORIES = INDUSTRIES.flatMap((ind) =>
  ind.categories.map((name) => ({
    id: `${ind.id}-${slugify(name)}`,
    name,
    compatibleWith: { industry: [ind.id] },
  }))
);

const REGISTRIES = {
  industry: INDUSTRIES.map(({ id, name, compatibleWith }) => ({ id, name, ...(compatibleWith ? { compatibleWith } : {}) })),
  category: CATEGORIES,
  purpose: PURPOSES,
  audience: AUDIENCES,
  brandPersonality: BRAND_PERSONALITIES,
  visualTheme: VISUAL_THEMES,
  layoutStyle: LAYOUT_STYLES,
  imageStyle: IMAGE_STYLES,
  colorMood: COLOR_MOODS,
  typographyStyle: TYPOGRAPHY_STYLES,
};

// Fail fast on malformed registry data — in development, not in a dropdown.
const fieldIds = TAXONOMY_FIELDS.map((f) => f.id);
Object.entries(REGISTRIES).forEach(([fieldId, items]) => {
  const { valid, errors } = validateRegistry(fieldId, items, fieldIds);
  if (!valid) throw new Error(`Taxonomy registry "${fieldId}" invalid: ${errors.join('; ')}`);
});

export const taxonomyEngine = createTaxonomyEngine({
  fields: TAXONOMY_FIELDS,
  registries: REGISTRIES,
});

export const getRegistry = (fieldId) => [...(REGISTRIES[fieldId] || [])];

/** Counts for docs, tests, and the admin report. */
export const taxonomyStats = () => ({
  industries: REGISTRIES.industry.length,
  categories: REGISTRIES.category.length,
  purposes: REGISTRIES.purpose.length,
  audiences: REGISTRIES.audience.length,
  brandPersonalities: REGISTRIES.brandPersonality.length,
  visualThemes: REGISTRIES.visualTheme.length,
  layoutStyles: REGISTRIES.layoutStyle.length,
  imageStyles: REGISTRIES.imageStyle.length,
  colorMoods: REGISTRIES.colorMood.length,
  typographyStyles: REGISTRIES.typographyStyle.length,
});

export { createTaxonomyEngine } from './taxonomyEngine';
export { validateRegistry, slugify } from './schema';
